import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';
import { store } from './store/store';
import Sidebar from './components/Sidebar';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import StudentRequests from './pages/StudentRequests';
import Countries from './pages/Countries';
import Cities from './pages/Cities';
import Universities from './pages/Universities';
import UniversityPrograms from './pages/UniversityPrograms';
import Scholarships from './pages/Scholarships';
import Consultants from './pages/Consultants';
import Students from './pages/Students';
import RequestStatuses from './pages/RequestStatuses';
import Contracts from './pages/Contracts';
import ContractServices from './pages/ContractServices';
import CashManagement from './pages/CashManagement';
import RoleManagement from './pages/RoleManagement';
import UserManagement from './pages/UserManagement';
import TaskManagement from './pages/TaskManagement';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { isAuthValid, clearAuthData } from './utils/authUtils';
import { useAuth } from './hooks/useAuth';
import { refreshAuth } from './store/authSlice';
import SessionMonitor from './components/SessionMonitor';

function AppContent() {
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();
  const { isAuthenticated, logout } = useAuth();

  // Check for existing authentication on app load
  useEffect(() => {
    const checkAuth = () => {
      if (isAuthValid()) {
        // Refresh auth state from localStorage
        dispatch(refreshAuth());
      } else {
        // Clean up any incomplete authentication data
        clearAuthData();
      }
      setIsLoading(false);
    };

    checkAuth();
  }, [dispatch]);

  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  // Show loading screen while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-blue-100">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Yüklənir...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      {!isAuthenticated ? (
        // Show login page if not authenticated
        <Routes>
          <Route path="*" element={<Login />} />
        </Routes>
      ) : (
        // Show main application if authenticated
        <div className="min-h-screen bg-gray-50">
          <Sidebar onLogout={handleLogout} />
          <SessionMonitor onLogout={handleLogout} />
          
          {/* Main content */}
          <div className="lg:pl-72">
            <main className="py-10">
              <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
              />
              
              <div className="px-4 sm:px-6 lg:px-8">
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  
                  {/* Dashboard - accessible to all authenticated users */}
                  <Route path="/dashboard" element={
                    <ProtectedRoute onLogout={handleLogout}>
                      <Dashboard />
                    </ProtectedRoute>
                  } />
                  
                  {/* Countries & Cities - now use specific permissions */}
                  <Route path="/countries" element={
                    <ProtectedRoute 
                      onLogout={handleLogout}
                      permission="country.view"
                    >
                      <Countries />
                    </ProtectedRoute>
                  } />
                  <Route path="/cities" element={
                    <ProtectedRoute 
                      onLogout={handleLogout}
                      permission="city.view"
                    >
                      <Cities />
                    </ProtectedRoute>
                  } />
                  
                  {/* Universities module - updated to match API permissions */}
                  <Route path="/universities" element={
                    <ProtectedRoute 
                      onLogout={handleLogout}
                      permission="university.view"
                    >
                      <Universities />
                    </ProtectedRoute>
                  } />
                  <Route path="/university-programs" element={
                    <ProtectedRoute 
                      onLogout={handleLogout}
                      permission="university_program.view"
                    >
                      <UniversityPrograms />
                    </ProtectedRoute>
                  } />
                  <Route path="/scholarships" element={
                    <ProtectedRoute 
                      onLogout={handleLogout}
                      permission="scholarship.view"
                    >
                      <Scholarships />
                    </ProtectedRoute>
                  } />
                  
                  {/* Students module - updated to match API permissions */}
                  <Route path="/students" element={
                    <ProtectedRoute 
                      onLogout={handleLogout}
                      permission="student.view"
                    >
                      <Students />
                    </ProtectedRoute>
                  } />
                  <Route path="/student-requests" element={
                    <ProtectedRoute 
                      onLogout={handleLogout}
                      permission="student_request.view"
                    >
                      <StudentRequests />
                    </ProtectedRoute>
                  } />
                  <Route path="/request-statuses" element={
                    <ProtectedRoute 
                      onLogout={handleLogout}
                      permission="student_request.view"
                    >
                      <RequestStatuses />
                    </ProtectedRoute>
                  } />
                  
                  {/* Contracts module - updated to match API permissions */}
                  <Route path="/contracts" element={
                    <ProtectedRoute 
                      onLogout={handleLogout}
                      permission="contract.view"
                    >
                      <Contracts />
                    </ProtectedRoute>
                  } />
                  <Route path="/contract-services" element={
                    <ProtectedRoute 
                      onLogout={handleLogout}
                      permission="contract.view"
                    >
                      <ContractServices />
                    </ProtectedRoute>
                  } />
                  
                  {/* Finance module - updated to match API permissions */}
                  <Route path="/cash-management" element={
                    <ProtectedRoute 
                      onLogout={handleLogout}
                      permission="cash_management.view"
                    >
                      <CashManagement />
                    </ProtectedRoute>
                  } />
                  
                  {/* User Management - FIXED: now uses correct API permission */}
                  <Route path="/consultants" element={
                    <ProtectedRoute 
                      onLogout={handleLogout}
                      permission="consultant.view"
                    >
                      <Consultants />
                    </ProtectedRoute>
                  } />
                  <Route path="/user-management" element={
                    <ProtectedRoute 
                      onLogout={handleLogout}
                      permission="user_management.view"
                    >
                      <UserManagement />
                    </ProtectedRoute>
                  } />
                  <Route path="/role-management" element={
                    <ProtectedRoute 
                      onLogout={handleLogout}
                      permission="role_management.view"
                    >
                      <RoleManagement />
                    </ProtectedRoute>
                  } />
                  <Route path="/task-management" element={
                    <ProtectedRoute 
                      onLogout={handleLogout}
                      permissions={['task_management.view', 'task_management.add', 'task_management.edit', 'task_management.delete', 'task_management.assign_others']}
                      requireAll={false}
                    >
                      <TaskManagement />
                    </ProtectedRoute>
                  } />
                  
                  {/* Catch any other routes and redirect to dashboard */}
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </div>
            </main>
          </div>
        </div>
      )}
    </Router>
  );
}

function App() {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
}

export default App;
